import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Check, X, Star } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { WEEK, DAY_LABELS, fmtWindow, money, isFeatured } from "@/lib/hh";
import { LoginForm } from "@/pages/Login";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

function useList(url, params) {
  const [rows, setRows] = useState(null);
  const key = JSON.stringify(params || {});
  const load = useCallback(() => {
    api.get(url, { params: JSON.parse(key) }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [url, key]);
  useEffect(() => {
    load();
  }, [load]);
  return [rows, load];
}

const act = async (fn, msg, reload) => {
  try {
    await fn();
    toast.success(msg);
    reload();
  } catch (e) {
    toast.error(errMsg(e));
  }
};

function SubmissionCard({ s, reload }) {
  return (
    <div data-testid={`submission-${s.id}`} className="panel p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-heading text-3xl text-white">{s.bar_name}</p>
          <p className="text-sm text-zinc-400">{s.address}, {s.city}, {s.state} · {s.phone}</p>
          <p className="text-sm text-zinc-500">Owner: {s.owner_name} · {s.email} · sent {fmtDate(s.created_at)}</p>
          {s.lat == null && <p className="mt-1 text-xs font-bold text-amber-400">Address could not be located on the map</p>}
        </div>
        <div className="flex gap-2">
          {s.status !== "approved" && <button data-testid={`approve-${s.id}`} onClick={() => act(() => api.post(`/admin/submissions/${s.id}/approve`), "Approved", reload)} className="flex items-center gap-1 rounded-full bg-emerald-500 px-4 py-2 text-sm font-bold text-zinc-950"><Check size={15} /> Approve</button>}
          {s.status !== "rejected" && <button data-testid={`reject-${s.id}`} onClick={() => act(() => api.post(`/admin/submissions/${s.id}/reject`), "Rejected", reload)} className="flex items-center gap-1 rounded-full border border-rose-500/60 px-4 py-2 text-sm font-bold text-rose-300"><X size={15} /> Reject</button>}
        </div>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <ul className="text-sm text-zinc-300">{WEEK.filter((d) => s.hours[d]).map((d) => <li key={d}>{DAY_LABELS[d]}: {fmtWindow(s.hours[d])}</li>)}</ul>
        <ul className="text-sm text-zinc-300">{s.specials.map((x, i) => <li key={i}>{x.item} — <span className="text-[#fde68a]">{money(x.price)}</span> ({x.category})</li>)}</ul>
      </div>
    </div>
  );
}

function Submissions() {
  const [status, setStatus] = useState("pending");
  const [rows, reload] = useList("/admin/submissions", { status });
  return (
    <div>
      <div className="mb-4 flex gap-2">
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s} data-testid={`subs-tab-${s}`} data-on={status === s} onClick={() => setStatus(s)} className="chip capitalize">{s}</button>
        ))}
      </div>
      {rows && !rows.length && <p data-testid="subs-empty" className="text-zinc-500">No {status} submissions.</p>}
      <div className="space-y-4">{(rows || []).map((s) => <SubmissionCard key={s.id} s={s} reload={reload} />)}</div>
    </div>
  );
}

function Listings() {
  const [rows, reload] = useList("/admin/listings");
  if (rows && !rows.length) return <p className="text-zinc-500">No approved listings yet.</p>;
  return (
    <div className="space-y-3">
      {(rows || []).map((v) => (
        <div key={v.id} data-testid={`listing-${v.id}`} className="panel flex flex-wrap items-center justify-between gap-3 p-4">
          <div>
            <p className="font-semibold text-white">{v.name} {isFeatured(v) && <Star size={13} className="inline text-amber-400" fill="currentColor" />}</p>
            <p className="text-sm text-zinc-500">{v.address} · {v.owner_email}</p>
            {isFeatured(v) && <p className="text-xs text-amber-400">Featured until {fmtDate(v.featured_until)}</p>}
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            Featured
            <Switch data-testid={`featured-toggle-${v.id}`} checked={isFeatured(v)} onCheckedChange={(on) => act(() => api.post(`/admin/listings/${v.id}/featured`, { on, days: 7 }), on ? "Featured for 7 days" : "Featured turned off", reload)} />
          </label>
        </div>
      ))}
    </div>
  );
}

function SponsorRow({ o, reload }) {
  const [img, setImg] = useState(o.image_url || "");
  const [link, setLink] = useState(o.link_url || "");
  return (
    <div data-testid={`sponsor-${o.id}`} className="panel p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="text-sm">
          <p className="font-semibold text-white">{o.business_name} · {o.state} · {o.weeks} wk · ${o.amount}</p>
          <p className="text-zinc-400">{o.contact_name} · {o.email} · {o.phone}</p>
          <p className="text-zinc-500">Runs {fmtDate(o.start_date)} – {fmtDate(o.end_date)} · requested {fmtDate(o.created_at)}</p>
        </div>
        <StatusPill o={o} reload={reload} />
      </div>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <Input data-testid={`sponsor-image-${o.id}`} placeholder="Ad photo URL" value={img} onChange={(e) => setImg(e.target.value)} className="bg-zinc-950" />
        <Input data-testid={`sponsor-link-${o.id}`} placeholder="Click-through link (optional)" value={link} onChange={(e) => setLink(e.target.value)} className="bg-zinc-950" />
        <button data-testid={`sponsor-save-${o.id}`} onClick={() => act(() => api.patch(`/admin/orders/${o.id}`, { image_url: img, link_url: link }), "Ad saved", reload)} className="chip justify-center">Save ad</button>
      </div>
    </div>
  );
}

function StatusPill({ o, reload }) {
  if (o.status === "paid") return <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-bold text-emerald-300">Paid {fmtDate(o.paid_at)}</span>;
  return (
    <button data-testid={`mark-paid-${o.id}`} onClick={() => act(() => api.post(`/admin/orders/${o.id}/mark-paid`), "Marked paid and activated", reload)} className="rounded-full border border-amber-500/60 px-3 py-1 text-xs font-bold text-amber-300">
      Awaiting payment · Mark paid
    </button>
  );
}

function Sponsors() {
  const [rows, reload] = useList("/admin/orders", { kind: "sponsor" });
  if (rows && !rows.length) return <p className="text-zinc-500">No sponsor requests yet.</p>;
  return <div className="space-y-3">{(rows || []).map((o) => <SponsorRow key={o.id} o={o} reload={reload} />)}</div>;
}

function Payments() {
  const [rows, reload] = useList("/admin/orders");
  const total = (rows || []).filter((o) => o.status === "paid").reduce((t, o) => t + o.amount, 0);
  return (
    <div>
      <p data-testid="payments-total" className="mb-4 text-zinc-300">Paid total: <span className="font-extrabold text-[#fde68a]">${total.toFixed(2)}</span></p>
      <div className="overflow-x-auto rounded-xl border border-zinc-800">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="bg-zinc-900 text-zinc-400"><tr><th className="p-3">Date</th><th className="p-3">Type</th><th className="p-3">For</th><th className="p-3">Email</th><th className="p-3">Amount</th><th className="p-3">Status</th></tr></thead>
          <tbody>
            {(rows || []).map((o) => (
              <tr key={o.id} data-testid={`payment-${o.id}`} className="border-t border-zinc-800">
                <td className="p-3 text-zinc-400">{fmtDate(o.created_at)}</td>
                <td className="p-3 capitalize text-zinc-200">{o.kind}</td>
                <td className="p-3 text-zinc-200">{o.kind === "featured" ? o.venue_name : `${o.business_name} (${o.state}, ${o.weeks} wk)`}</td>
                <td className="p-3 text-zinc-400">{o.email}</td>
                <td className="p-3 font-bold text-[#fde68a]">${o.amount.toFixed(2)}</td>
                <td className="p-3"><StatusPill o={o} reload={reload} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  if (user === null) return <div className="page" />;
  if (!user || user.role !== "admin") {
    return (
      <div className="page max-w-md">
        <h1 className="font-heading flex items-center gap-2 text-5xl text-white"><ShieldCheck className="text-[#fde68a]" /> Admin</h1>
        {user && <p data-testid="admin-denied" className="mt-2 text-rose-400">This account doesn't have admin access.</p>}
        {!user && <div className="panel mt-6 p-6"><LoginForm mode="login" testPrefix="admin-login" /></div>}
      </div>
    );
  }
  return (
    <div className="page">
      <h1 className="font-heading text-5xl text-white">Admin Dashboard</h1>
      <Tabs defaultValue="subs" className="mt-6">
        <TabsList className="no-scrollbar flex w-full justify-start overflow-x-auto">
          <TabsTrigger value="subs" data-testid="admin-tab-submissions">Submissions</TabsTrigger>
          <TabsTrigger value="listings" data-testid="admin-tab-listings">Listings & Featured</TabsTrigger>
          <TabsTrigger value="sponsors" data-testid="admin-tab-sponsors">Sponsors</TabsTrigger>
          <TabsTrigger value="payments" data-testid="admin-tab-payments">Payments</TabsTrigger>
        </TabsList>
        <TabsContent value="subs" className="mt-6"><Submissions /></TabsContent>
        <TabsContent value="listings" className="mt-6"><Listings /></TabsContent>
        <TabsContent value="sponsors" className="mt-6"><Sponsors /></TabsContent>
        <TabsContent value="payments" className="mt-6"><Payments /></TabsContent>
      </Tabs>
    </div>
  );
}
